import { useState } from "react"
import { IconCalendarX, IconLoader2, IconRefresh } from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { useGetBookingQuery } from "@/app/booking-calendar/bookingCalendarApi"
import { AppointmentDialog } from "@/app/booking-calendar/components/modals"
import { SITE_TIME_ZONE } from "@/app/booking-calendar/lib/siteTime"
import { useListCancellationsQuery } from "@/app/booking-calendar/api/cancellationsApi"
import type { CancellationListItem, CancellationQueryMode } from "@/app/booking-calendar/api/cancellationsApi"
import { describeCanceller } from "./canceller"
import { defaultWindowFromSep1, formatInZone, validateWindow } from "./pacificWindow"

const PAGE_SIZE = 50

const MODE_HELP: Record<CancellationQueryMode, string> = {
  CancelledInWindow: "Bookings cancelled during the window, whenever the appointment was.",
  ScheduledInWindow: "Appointments that were scheduled during the window and are now cancelled, whenever that happened.",
}

/**
 * Cancelled appointments, for administrators (`appointments:manage`).
 *
 * Two Pacific wall-clock pickers bound a half-open window — the start is included, the end is not —
 * and the toggle says what the window is on: when the booking was cancelled (default), or when the
 * appointment was scheduled. Every time on the page is shown on the site's clock, whatever the
 * browser's zone. The window opens (and Reset returns it) on the most recent September 1 through the
 * end of today (AB#835). Choosing a row opens the same appointment dialog the calendar uses.
 */
export default function CancellationsPage() {
  const [initial] = useState(() => defaultWindowFromSep1())
  const [start, setStart] = useState(initial.start)
  const [end, setEnd] = useState(initial.end)
  const [mode, setMode] = useState<CancellationQueryMode>("CancelledInWindow")
  const [page, setPage] = useState(1)
  const [selected, setSelected] = useState<CancellationListItem | null>(null)

  const range = validateWindow(start, end)

  const query = useListCancellationsQuery(
    range.ok
      ? { windowStart: range.start, windowEnd: range.end, mode, page, pageSize: PAGE_SIZE }
      : { windowStart: "", windowEnd: "", mode, page, pageSize: PAGE_SIZE },
    { skip: !range.ok },
  )

  const selectedBooking = useGetBookingQuery(selected?.calBookingUid ?? "", { skip: !selected?.calBookingUid })

  const rows = query.data?.items ?? []
  const total = query.data?.total ?? 0
  const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE))

  const open = (row: CancellationListItem) => {
    if (row.calBookingUid) setSelected(row)
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <IconCalendarX className="size-6" />
          <h1 className="text-2xl font-bold">Cancellations</h1>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => void query.refetch()}
          disabled={!range.ok || query.isFetching || query.isUninitialized}
        >
          {query.isFetching ? <IconLoader2 className="size-4 animate-spin" /> : <IconRefresh className="size-4" />}
          Refresh
        </Button>
      </div>
      <p className="text-muted-foreground max-w-3xl">
        Cancelled appointments in a window of Pacific time. The window includes its start and excludes
        its end. {MODE_HELP[mode]}
      </p>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="window-start" className="text-xs">From (Pacific)</Label>
          <Input
            id="window-start"
            type="datetime-local"
            className="w-56"
            value={start}
            onChange={event => { setStart(event.target.value); setPage(1) }}
            aria-invalid={!range.ok || undefined}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="window-end" className="text-xs">To (Pacific)</Label>
          <Input
            id="window-end"
            type="datetime-local"
            className="w-56"
            value={end}
            onChange={event => { setEnd(event.target.value); setPage(1) }}
            aria-invalid={!range.ok || undefined}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium">Window is on</span>
          <ToggleGroup
            type="single"
            variant="outline"
            value={mode}
            onValueChange={value => {
              if (value) {
                setMode(value as CancellationQueryMode)
                setPage(1)
              }
            }}
            aria-label="What the window filters on"
          >
            <ToggleGroupItem value="CancelledInWindow" className="px-3">When cancelled</ToggleGroupItem>
            <ToggleGroupItem value="ScheduledInWindow" className="px-3">When scheduled</ToggleGroupItem>
          </ToggleGroup>
        </div>
        {(start !== initial.start || end !== initial.end || mode !== "CancelledInWindow") && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { setStart(initial.start); setEnd(initial.end); setMode("CancelledInWindow"); setPage(1) }}
          >
            Reset
          </Button>
        )}
      </div>

      {!range.ok ? (
        <div className="rounded-xl border bg-card p-6 text-sm text-destructive" role="alert">
          {range.error}
        </div>
      ) : query.isLoading ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : query.isError ? (
        <div className="flex flex-col items-start gap-2 rounded-xl border bg-card p-6">
          <p className="text-sm text-destructive">The cancellations could not be loaded.</p>
          <Button variant="outline" size="sm" onClick={() => void query.refetch()}>Retry</Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border bg-card p-6 text-muted-foreground">
          {mode === "CancelledInWindow"
            ? "Nothing was cancelled in this window."
            : "No appointment scheduled in this window has been cancelled."}
        </div>
      ) : (
        <>
          <div className="rounded-xl border bg-card">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Booking</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Original start</TableHead>
                  <TableHead>Cancelled at</TableHead>
                  <TableHead>Cancelled by</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map(row => (
                  <TableRow
                    key={row.bookingRequestId}
                    className={row.calBookingUid ? "cursor-pointer align-top" : "align-top"}
                    tabIndex={row.calBookingUid ? 0 : undefined}
                    onClick={() => open(row)}
                    onKeyDown={event => {
                      if (row.calBookingUid && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault()
                        open(row)
                      }
                    }}
                  >
                    <TableCell>
                      <div className="font-mono text-xs">{row.referenceCode ?? "—"}</div>
                      <div className="font-mono text-xs text-muted-foreground" title="Booking request ID">
                        {row.bookingRequestId}
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{row.customerName || "—"}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatInZone(row.originalStart)}</TableCell>
                    <TableCell className="whitespace-nowrap">{formatInZone(row.cancelledAt)}</TableCell>
                    <TableCell>{describeCanceller(row)}</TableCell>
                    <TableCell className="max-w-sm whitespace-normal">
                      <CancellationReason row={row} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(total, page * PAGE_SIZE)} of {total}
            </span>
            {total > PAGE_SIZE && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</Button>
                <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>Next</Button>
              </div>
            )}
          </div>
        </>
      )}

      {selected && selectedBooking.isError && (
        <div className="flex items-center justify-between gap-2 rounded-xl border bg-card p-4 text-sm">
          <span className="text-destructive">That appointment could not be loaded from the scheduling system.</span>
          <Button variant="outline" size="sm" onClick={() => setSelected(null)}>Dismiss</Button>
        </div>
      )}

      {selected && (
        <AppointmentDialog
          booking={selectedBooking.data}
          staffName={undefined}
          timeZone={SITE_TIME_ZONE}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  )
}

/** Why the appointment was cancelled. */
function CancellationReason({ row }: { row: CancellationListItem }) {
  if (!row.cancellationReason) {
    return <span className="text-muted-foreground">—</span>
  }
  return <span className="text-sm">{row.cancellationReason}</span>
}
